import type { AuditEntry } from '@school-erp/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { apiList } from '../../api/http';
import { t } from '../../i18n';

const PAGE_SIZE = 25;

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');

  const audit = useQuery({
    queryKey: ['audit', { page, action }],
    queryFn: ({ signal }) =>
      apiList<AuditEntry>('/audit-logs', {
        query: { page, pageSize: PAGE_SIZE, action: action.trim() },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const totalPages = audit.data?.meta.totalPages ?? 1;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('audit.title')}</h1>

      <div>
        <label htmlFor="audit-action" className="block text-sm font-medium">
          {t('audit.filterAction')}
        </label>
        <input
          id="audit-action"
          value={action}
          placeholder="auth.login"
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
          className="mt-1 w-full max-w-sm rounded border border-slate-300 bg-white px-3 py-2 text-sm"
        />
      </div>

      {audit.isPending && <p role="status">{t('audit.loading')}</p>}
      {audit.isError && (
        <p role="alert" className="text-red-700">
          {t('audit.error')}
        </p>
      )}

      {audit.data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t('audit.when')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('audit.action')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('audit.entity')}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t('audit.ip')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {audit.data.data.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-slate-600">
                      {t('audit.empty')}
                    </td>
                  </tr>
                )}
                {audit.data.data.map((entry) => (
                  <tr key={entry.id}>
                    <td className="whitespace-nowrap px-4 py-3">
                      <time dateTime={entry.occurredAt}>
                        {new Date(entry.occurredAt).toLocaleString()}
                      </time>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{entry.action}</td>
                    <td className="px-4 py-3">
                      {entry.entity ? `${entry.entity} ${entry.entityId?.slice(0, 8) ?? ''}` : '—'}
                    </td>
                    <td className="px-4 py-3">{entry.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav aria-label="Pagination" className="flex items-center justify-between text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded border border-slate-300 px-3 py-1.5 disabled:opacity-50"
            >
              {t('users.prev')}
            </button>
            <span>{t('users.page', { page, total: totalPages })}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-slate-300 px-3 py-1.5 disabled:opacity-50"
            >
              {t('users.next')}
            </button>
          </nav>
        </>
      )}
    </div>
  );
}
